/**
 * Snapchat Advertising REST helpers (Story 19.7, Task 2)
 *
 * Helper functions for the Snap Marketing API v1 endpoints used during OAuth
 * post-callback validation:
 *
 *   - GET /v1/me                                                — authenticated user profile
 *   - GET /v1/me/organizations?with_ad_accounts=true            — flatten orgs + ad accounts in one request
 *
 * Snap uses a single credential per request (Bearer access token); there is
 * no Developer Token like Microsoft Ads.
 *
 * Note: The token endpoint lives on `accounts.snapchat.com` (separate host,
 * separate quota), but the data API lives on `adsapi.snapchat.com`.
 *
 * @see https://developers.snap.com/api/marketing-api/Ads-API/organizations
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { SnapchatAdsApiCallError, parseSnapchatAdsApiError } from "./errors";

// ============================================================================
// Constants
// ============================================================================

export const SNAPCHAT_ADS_API_BASE = "https://adsapi.snapchat.com/v1";

const API_TIMEOUT_MS = 25_000;

/**
 * Defence-in-depth caps for organization + ad-account discovery. Snap doesn't
 * document pagination on the `/me/organizations?with_ad_accounts=true`
 * endpoint, so we cap at 100 orgs and 200 flattened ad accounts. If we hit
 * the cap we set `partialDiscovery=true` and the route surfaces a
 * `&warning=partial-discovery` query param so the UI can warn the user.
 */
export const SNAPCHAT_MAX_ORGS = 100;
export const SNAPCHAT_MAX_AD_ACCOUNTS = 200;

/**
 * Hard ceiling on pages followed via `paging.next_link` (Story 31.14 AC #5a).
 *
 * Snap doesn't document pagination on `/me/organizations?with_ad_accounts=true`
 * today — `paging.next_link` is the platform-wide list pattern, so we
 * TOLERATE it if present rather than assume it. The page bound (together
 * with the seen-URL loop guard) guarantees a malformed/self-referencing
 * cursor can never spin the 31-24 re-discovery cron in a tight loop.
 * 25 pages comfortably exceeds the org/ad-account caps at any realistic
 * page size.
 */
export const SNAPCHAT_MAX_DISCOVERY_PAGES = 25;

// ============================================================================
// Types
// ============================================================================

export interface SnapchatAdsUserInfo {
  /** Snap user id (UUID) */
  id: string;
  /** User email — best-effort, may be empty */
  email: string;
  /** Display name — best-effort, may be empty */
  displayName: string;
  /** Organization IDs the user belongs to (may be empty if user has no orgs) */
  organizationIds: string[];
}

export interface SnapchatAdAccount {
  /** UUID, e.g. "8e37b69c-9bb6-4cfd-99ce-0f4f5e3e1b9c" */
  id: string;
  /** Display name set by the advertiser */
  name: string;
  /** ISO 4217 currency code, e.g. "USD" */
  currency: string;
  /** IANA timezone, e.g. "America/Los_Angeles" */
  timezone: string;
  /** Account type, e.g. "PARTNER" / "DIRECT" */
  type: string;
  /** Account status, e.g. "ACTIVE" / "PAUSED" */
  status: string;
  /** Roles the OAuth user has on this account, e.g. ["ADMIN"] / ["REPORTS"] / ["CREATIVE"] */
  roles: string[];
  /** Parent organization id (UUID) */
  organizationId: string;
}

export interface SnapchatOrganization {
  id: string;
  name: string;
  type: string;
  adAccounts: SnapchatAdAccount[];
}

export interface SnapchatOrganizationsResult {
  organizations: SnapchatOrganization[];
  /** Flat list of every ad account, sorted ascending by `id` (lex-asc — Snap IDs are UUIDs) */
  flatAdAccounts: SnapchatAdAccount[];
  /** True when MAX_ORGS or MAX_AD_ACCOUNTS cap was hit during discovery */
  partialDiscovery: boolean;
  /** Total ad-account count BEFORE truncation (so the UI can say "X of N") */
  totalAdAccountCount: number;
}

// ============================================================================
// Header Builder
// ============================================================================

/**
 * Builds Snap Marketing API v1 REST headers.
 *
 * Snap only requires a single auth header (`Authorization: Bearer ...`).
 * Unlike Microsoft Ads there is no second universal credential.
 */
export function buildSnapchatHeaders(
  accessToken: string
): Record<string, string> {
  if (!accessToken) {
    throw new Error("accessToken is required for Snapchat Ads API calls");
  }
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// ============================================================================
// Response shapes (raw Snap envelopes)
// ============================================================================

interface RawMeResponse {
  request_status?: string;
  me?: {
    id?: string;
    email?: string;
    display_name?: string;
    organization_ids?: string[];
  };
}

interface RawAdAccount {
  id?: string;
  name?: string;
  currency?: string;
  timezone?: string;
  type?: string;
  status?: string;
  roles?: string[];
}

interface RawOrgEntry {
  organization?: {
    id?: string;
    name?: string;
    type?: string;
    ad_accounts?: Array<{ adaccount?: RawAdAccount } | RawAdAccount>;
  };
}

interface RawOrganizationsResponse {
  request_status?: string;
  organizations?: RawOrgEntry[];
  /**
   * Snap's platform-wide list-pagination envelope (Story 31.14 AC #5).
   * Undocumented on `/me/organizations` today — tolerated if present.
   * `next_link` carries the FULL next-page URL.
   */
  paging?: { next_link?: string };
}

/**
 * Unwraps Snap's dual ad-account envelope shapes: some responses nest the
 * account under `{ adaccount: {...} }`, others return the bare object.
 * Shared by the page-accumulation loop, the H1 honesty counter, and the
 * org flattening pass so all three tolerate both shapes identically.
 *
 * Never returns null/undefined: a `null` array entry (or a `{ adaccount:
 * null }` wrapper) yields `{}` so callers can safely read `.id` — the
 * id-validity check then skips the entry. (31.14 code review: previously a
 * `null` entry threw a TypeError out of the parse pass despite the
 * "tolerate defensively" contract.)
 */
function unwrapRawAdAccount(
  aRaw: { adaccount?: RawAdAccount } | RawAdAccount | null | undefined
): RawAdAccount {
  if (!aRaw || typeof aRaw !== "object") return {};
  return "adaccount" in aRaw
    ? ((aRaw as { adaccount?: RawAdAccount }).adaccount ?? {})
    : (aRaw as RawAdAccount);
}

/** Counts entries with a valid (non-empty string) ad-account id. */
function countValidAdAccounts(entries: RawOrgEntry[]): number {
  let count = 0;
  for (const entry of entries) {
    const list = entry?.organization?.ad_accounts;
    if (!Array.isArray(list)) continue;
    for (const aRaw of list) {
      const a = unwrapRawAdAccount(aRaw);
      if (typeof a.id === "string" && a.id.length > 0) count += 1;
    }
  }
  return count;
}

// ============================================================================
// User account
// ============================================================================

/**
 * Calls GET /v1/me and normalises the response into a stable shape.
 *
 * Used immediately after token exchange to enrich structured logging — the
 * actual ads-access decision is made in `fetchSnapchatAdsOrganizationsWithAdAccounts`.
 */
export async function fetchSnapchatAdsMe(
  accessToken: string
): Promise<SnapchatAdsUserInfo> {
  const headers = buildSnapchatHeaders(accessToken);

  const result = await safeFetch(
    `${SNAPCHAT_ADS_API_BASE}/me`,
    { method: "GET", headers },
    API_TIMEOUT_MS
  );

  if (!result.success) {
    throw buildSnapchatAdsApiError(
      result.error,
      "Snapchat Ads /me fetch failed"
    );
  }

  const response = result.data;

  let data: RawMeResponse;
  try {
    data = (await response.json()) as RawMeResponse;
  } catch {
    throw new Error("Invalid Snapchat Ads /me response: not JSON");
  }

  const me = data.me ?? {};
  return {
    id: me.id ?? "",
    email: me.email ?? "",
    displayName: me.display_name ?? "",
    organizationIds: Array.isArray(me.organization_ids)
      ? me.organization_ids.filter((s): s is string => typeof s === "string")
      : [],
  };
}

// ============================================================================
// Organizations + ad accounts
// ============================================================================

/**
 * Enumerates every organization + ad account this OAuth grant can access in
 * a single request via `/v1/me/organizations?with_ad_accounts=true`.
 *
 * Snap's `with_ad_accounts=true` flag returns the full hierarchy without
 * separate organization-by-organization fan-out — saves 1+N requests per
 * connect.
 *
 * Returns a flattened ad-account list sorted ascending by `id` (lex-asc —
 * Snap ad-account IDs are UUIDs, so lex sort is deterministic and stable
 * across re-auths regardless of API response ordering).
 *
 * Defensive caps: 100 orgs / 200 flattened ad accounts. Hitting either cap
 * sets `partialDiscovery=true` on the result.
 *
 * **Bounded pagination tolerance (Story 31.14 AC #5):** follows
 * `paging.next_link` when Snap returns it (tolerate-if-present — the
 * platform-wide list pattern is not documented on this endpoint today),
 * accumulating pages until exhaustion, the org/ad-account caps, or the
 * `SNAPCHAT_MAX_DISCOVERY_PAGES` bound. Hard rules:
 *  - loop guard: a `next_link` repeating an already-fetched URL terminates
 *    the loop (treated as end-of-list — some APIs echo the final page's own
 *    URL; flagging it partial would false-positive every connect);
 *  - origin pin: `next_link` is only followed when its origin is exactly
 *    `https://adsapi.snapchat.com` — a cross-host link is REFUSED (never
 *    send the Bearer header to a host the cursor names; discovery inputs
 *    are untrusted) and treated as end-of-list;
 *  - stopping early while a valid `next_link` remained (page bound or caps)
 *    sets `partialDiscovery=true` — H1-style honesty about truncation;
 *  - absent `paging` → identical single-request behaviour to pre-31.14.
 */
export async function fetchSnapchatAdsOrganizationsWithAdAccounts(
  accessToken: string
): Promise<SnapchatOrganizationsResult> {
  const headers = buildSnapchatHeaders(accessToken);

  const firstUrl = new URL(`${SNAPCHAT_ADS_API_BASE}/me/organizations`);
  firstUrl.searchParams.set("with_ad_accounts", "true");

  const rawOrgs: RawOrgEntry[] = [];
  const seenUrls = new Set<string>();
  let nextUrl: string | null = firstUrl.toString();
  let pagesFetched = 0;
  let morePagesRemained = false;

  while (nextUrl) {
    if (seenUrls.has(nextUrl)) {
      // Loop guard (AC #5a): a repeated cursor must never spin the 31-24
      // cron. Treat as end-of-list — no partial flag (could be a benign
      // "last page points at itself" provider quirk).
      break;
    }
    if (pagesFetched >= SNAPCHAT_MAX_DISCOVERY_PAGES) {
      // Page bound hit while a valid next_link remained → honestly partial.
      morePagesRemained = true;
      break;
    }
    seenUrls.add(nextUrl);

    const result = await safeFetch(
      nextUrl,
      { method: "GET", headers },
      API_TIMEOUT_MS
    );

    if (!result.success) {
      throw buildSnapchatAdsApiError(
        result.error,
        "Snapchat Ads /me/organizations fetch failed"
      );
    }

    let data: RawOrganizationsResponse;
    try {
      data = (await result.data.json()) as RawOrganizationsResponse;
    } catch {
      throw new Error(
        "Invalid Snapchat Ads /me/organizations response: not JSON"
      );
    }
    pagesFetched += 1;

    if (Array.isArray(data.organizations)) {
      rawOrgs.push(...data.organizations);
    }

    const rawNextLink =
      typeof data.paging?.next_link === "string" &&
      data.paging.next_link.length > 0
        ? data.paging.next_link
        : null;
    if (!rawNextLink) {
      break; // No paging envelope (today's documented shape) or exhausted.
    }

    // Origin pin (AC #5b): NEVER follow a next_link to another host — the
    // request carries `Authorization: Bearer ...` and a hostile/buggy cursor
    // must not be able to exfiltrate it. Malformed URLs are refused too.
    let parsedNext: URL;
    try {
      parsedNext = new URL(rawNextLink);
    } catch {
      break; // Malformed next_link → refuse, treat as end-of-list.
    }
    if (parsedNext.origin !== "https://adsapi.snapchat.com") {
      break; // Cross-host next_link → refuse, treat as end-of-list.
    }

    // Cap check (AC #5): once the org or ad-account cap is already reached,
    // fetching further pages is pointless — stop and flag partial since a
    // valid next_link remained.
    if (
      rawOrgs.length >= SNAPCHAT_MAX_ORGS ||
      countValidAdAccounts(rawOrgs) >= SNAPCHAT_MAX_AD_ACCOUNTS
    ) {
      morePagesRemained = true;
      break;
    }

    nextUrl = rawNextLink;
  }

  const truncatedOrgs = rawOrgs.slice(0, SNAPCHAT_MAX_ORGS);
  const partialOrgsTruncated = rawOrgs.length > SNAPCHAT_MAX_ORGS;

  const organizations: SnapchatOrganization[] = [];
  const flatAdAccounts: SnapchatAdAccount[] = [];
  let totalAdAccountCount = 0;
  let partialAdAccountsTruncated = false;

  // H1 fix: count ad accounts across ALL orgs (including ones we'll truncate
  // away under MAX_ORGS) so `totalAdAccountCount` honestly reports the full
  // size of the OAuth grant. The "X of N" UI hint is meaningless if N silently
  // drops the orgs we never enumerated.
  for (let i = SNAPCHAT_MAX_ORGS; i < rawOrgs.length; i++) {
    const dropped = rawOrgs[i]?.organization?.ad_accounts;
    if (Array.isArray(dropped)) {
      for (const aRaw of dropped) {
        const a = unwrapRawAdAccount(aRaw);
        if (typeof a.id === "string" && a.id.length > 0) {
          totalAdAccountCount += 1;
        }
      }
    }
  }

  for (const entry of truncatedOrgs) {
    const org = entry.organization;
    if (!org) continue;
    const orgId = typeof org.id === "string" ? org.id : "";
    if (!orgId) continue;

    const adAccountsRaw = Array.isArray(org.ad_accounts) ? org.ad_accounts : [];
    const adAccounts: SnapchatAdAccount[] = [];

    for (const aRaw of adAccountsRaw) {
      // Snap nests ad accounts under `{ adaccount: {...} }` in some envelope
      // shapes; tolerate both forms defensively.
      const a = unwrapRawAdAccount(aRaw);
      const aid = typeof a.id === "string" ? a.id : "";
      if (!aid) continue;
      totalAdAccountCount += 1;

      const account: SnapchatAdAccount = {
        id: aid,
        name: typeof a.name === "string" ? a.name : "",
        currency: typeof a.currency === "string" ? a.currency : "",
        timezone: typeof a.timezone === "string" ? a.timezone : "",
        type: typeof a.type === "string" ? a.type : "",
        status: typeof a.status === "string" ? a.status : "",
        roles: Array.isArray(a.roles)
          ? a.roles.filter((r): r is string => typeof r === "string")
          : [],
        organizationId: orgId,
      };
      adAccounts.push(account);
      if (flatAdAccounts.length < SNAPCHAT_MAX_AD_ACCOUNTS) {
        flatAdAccounts.push(account);
      } else {
        partialAdAccountsTruncated = true;
      }
    }

    organizations.push({
      id: orgId,
      name: typeof org.name === "string" ? org.name : "",
      type: typeof org.type === "string" ? org.type : "",
      adAccounts,
    });
  }

  // Sort flat list ascending by id for deterministic primary selection.
  // Snap ad-account IDs are UUIDs; localeCompare is stable and deterministic.
  flatAdAccounts.sort((a, b) => a.id.localeCompare(b.id));

  return {
    organizations,
    flatAdAccounts,
    partialDiscovery:
      partialOrgsTruncated || partialAdAccountsTruncated || morePagesRemained,
    totalAdAccountCount,
  };
}

// ============================================================================
// Internal: build a typed SnapchatAdsApiCallError from a safeFetch error
// ============================================================================

function buildSnapchatAdsApiError(
  safeFetchError: {
    message: string;
    providerMessage?: string;
    providerStatus?: number;
  },
  prefix: string
): SnapchatAdsApiCallError {
  let parsedBody: unknown = {};
  if (safeFetchError.providerMessage) {
    try {
      parsedBody = JSON.parse(safeFetchError.providerMessage);
    } catch {
      // not JSON — fall back to status-only mapping
    }
  }
  const parsed = parseSnapchatAdsApiError(
    parsedBody,
    safeFetchError.providerStatus
  );
  return new SnapchatAdsApiCallError(
    parsed ?? {
      type: "unknown",
      message: sanitizeErrorMessage(safeFetchError.message),
      status: safeFetchError.providerStatus ?? null,
      retryable: false,
    },
    prefix
  );
}
